import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { localHLC, repairStoredHlcs } from './hlc'
import { syncEngine } from './syncEngine'
import { keyManager } from '../crypto/keyManager'

// A keyed collection that syncs over the encrypted per-key settings channel,
// one row per item. Each row has its own last-writer-wins clock, so changes
// to different items on different devices never overwrite each other, and no
// server changes are needed. The settings store ignores keys it does not own.

interface SyncedRow<T> {
  /** null marks a deletion, kept so it can reach other devices. */
  value: T | null
  /** HLC of the latest local or applied remote change. */
  hlc: number
  /** HLC last confirmed as sent to, or received from, the cloud. */
  syncedHlc: number
}

export type RemoteSettings = Record<string, { value: any; hlc: number }> | null | undefined

export interface SyncedRowsOptions<T> {
  /** localStorage key for this device's copy. */
  storageKey: string
  /** Prefix of this collection's rows in the settings channel, e.g. 'calendar.event.'. */
  remotePrefix: string
  /** Validates and normalizes a value read from the cloud; null rejects it. */
  parse: (raw: unknown) => T | null
  /** Delay before local changes are sent. */
  pushDelayMs?: number
}

export interface SyncedRows<T> {
  rows: Ref<Record<string, SyncedRow<T>>>
  /** Live values, without deletions. */
  values: ComputedRef<Record<string, T>>
  write: (id: string, value: T | null) => void
  pushPending: () => Promise<void>
  syncWithRemote: (remote: RemoteSettings) => Promise<void>
}

function loadRows<T>(storageKey: string): Record<string, SyncedRow<T>> {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}')
    if (!parsed || typeof parsed !== 'object') return {}
    let repaired = false
    for (const row of Object.values(parsed) as SyncedRow<T>[]) {
      const fixed = repairStoredHlcs(row.hlc ?? 0, row.syncedHlc ?? 0)
      if (fixed) { Object.assign(row, fixed); repaired = true }
    }
    if (repaired) localStorage.setItem(storageKey, JSON.stringify(parsed))
    return parsed
  } catch {
    return {}
  }
}

/** Without the master key a push would upload an empty value, so it waits. */
export function canSyncRows(): boolean {
  return syncEngine.isAuthenticated() && !!keyManager.getMasterKey()
}

export function createSyncedRows<T>(options: SyncedRowsOptions<T>): SyncedRows<T> {
  const { storageKey, remotePrefix, parse, pushDelayMs = 800 } = options
  const rows = ref(loadRows<T>(storageKey)) as Ref<Record<string, SyncedRow<T>>>

  const values = computed(() => {
    const live: Record<string, T> = {}
    for (const [id, row] of Object.entries(rows.value)) {
      if (row.value !== null) live[id] = row.value
    }
    return live
  })

  function persist() {
    localStorage.setItem(storageKey, JSON.stringify(rows.value))
  }

  // Pushes a fixed delay after the first unsent change rather than after the
  // last, so steady writing still reaches the cloud; later changes ride along.
  let pushTimer: ReturnType<typeof setTimeout> | null = null
  function schedulePush() {
    if (pushTimer) return
    pushTimer = setTimeout(() => {
      pushTimer = null
      void pushPending()
    }, pushDelayMs)
  }

  function write(id: string, value: T | null) {
    rows.value = {
      ...rows.value,
      [id]: { value, hlc: localHLC.now(), syncedHlc: rows.value[id]?.syncedHlc ?? 0 },
    }
    persist()
    schedulePush()
  }

  /** Sends local changes the cloud has not seen; the outbox covers being offline. */
  async function pushPending() {
    if (!canSyncRows()) return
    const pending = Object.entries(rows.value).filter(([, row]) => row.hlc > row.syncedHlc)
    if (pending.length === 0) return

    await syncEngine.pushSettings(pending.map(([id, row]) => ({
      key: remotePrefix + id,
      value: { value: row.value },
      hlc: row.hlc,
    })))

    const next = { ...rows.value }
    for (const [id, row] of pending) {
      // Only mark what was sent; a change made during the request stays pending.
      if (next[id]?.hlc === row.hlc) next[id] = { ...next[id], syncedHlc: row.hlc }
    }
    rows.value = next
    persist()
  }

  /** Merges this collection's rows from a settings pull, then sends local changes. */
  async function syncWithRemote(remote: RemoteSettings) {
    if (remote) {
      const next = { ...rows.value }
      let changed = false
      for (const [key, item] of Object.entries(remote)) {
        if (!key.startsWith(remotePrefix) || !item?.value || !item.hlc) continue
        if (!('value' in item.value)) continue
        const id = key.slice(remotePrefix.length)
        if (next[id] && next[id].hlc >= item.hlc) continue

        const raw = item.value.value
        const value = raw === null ? null : parse(raw)
        if (raw !== null && value === null) continue
        next[id] = { value, hlc: item.hlc, syncedHlc: item.hlc }
        changed = true
      }
      if (changed) {
        rows.value = next
        persist()
      }
    }
    await pushPending()
  }

  return { rows, values, write, pushPending, syncWithRemote }
}
